import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import type { Colaboracion, EstadoColaboracion } from "../dominio/tipos";
import { formatoFechaHora } from "../negocios/formato";
import { useAbrirFicha } from "../negocios/useAbrirFicha";
import { PaginaAdmin } from "./PaginaAdmin";

/**
 * 43 (Robert, 28/10/2026): las propuestas de colaboración de la web (/colaboradores), aparte de la cola de
 * ventas. Solo Jan y Robert. Los datos van en texto (se pueden seleccionar y copiar). «Pasar a ficha» si alguna
 * acaba en cliente; «Descartar» o «Vista» para llevar la cuenta.
 */
export const ColaboracionesPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const esAdmin = useEsAdmin();
  const abrirFicha = useAbrirFicha();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [ocupada, setOcupada] = useState<string | null>(null);
  const clave = ["nevora", "colaboraciones"];
  const { data, isPending } = useQuery({
    queryKey: clave,
    queryFn: () => dataProvider.colaboraciones(),
    enabled: esAdmin,
  });
  const t = (k: string, o?: Record<string, unknown>) =>
    translate(`nevora.colaboraciones.${k}`, o);

  const accion = async (c: Colaboracion, hacer: () => Promise<unknown>) => {
    setOcupada(String(c.id));
    try {
      await hacer();
      await queryClient.invalidateQueries({ queryKey: clave });
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setOcupada(null);
    }
  };
  const estado = (c: Colaboracion, e: EstadoColaboracion) =>
    accion(c, () => dataProvider.marcarColaboracion(c.id, e));
  const aFicha = (c: Colaboracion) =>
    accion(c, async () => {
      const id = await dataProvider.colaboracionAFicha(c.id);
      notify(t("en_ficha"), { type: "success" });
      if (id != null) abrirFicha(id);
    });

  return (
    <PaginaAdmin titulo="nevora.colaboraciones.titulo" ayuda="nevora.colaboraciones.ayuda">
      {isPending ? null : data === null ? (
        <p className="text-sm text-muted-foreground">{t("sin_base")}</p>
      ) : !data?.length ? (
        <p className="text-sm text-muted-foreground">{t("ninguna")}</p>
      ) : (
        <ul className="space-y-3" data-testid="colaboraciones">
          {data.map((c) => (
            <li key={String(c.id)} className="rounded-lg border bg-card px-4 py-3 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{c.nombre}</span>
                {c.empresa ? <span className="text-sm">· {c.empresa}</span> : null}
                <Badge variant={c.estado === "nueva" ? "default" : "outline"}>
                  {t(`estado_${c.estado}`)}
                </Badge>
                <span className="ml-auto text-xs text-muted-foreground">
                  {formatoFechaHora(c.recibida_at)}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {[c.email, c.telefono, c.dedicacion, c.fuente ?? c.pagina]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {c.mensaje ? <p className="text-sm whitespace-pre-line">{c.mensaje}</p> : null}
              <div className="flex flex-wrap gap-2 pt-1">
                {c.negocio_id != null ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => abrirFicha(c.negocio_id!)}
                  >
                    {t("ver_ficha")}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    disabled={ocupada === String(c.id)}
                    onClick={() => aFicha(c)}
                  >
                    {t("pasar_a_ficha")}
                  </Button>
                )}
                {c.estado === "nueva" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={ocupada === String(c.id)}
                    onClick={() => estado(c, "vista")}
                  >
                    {t("marcar_vista")}
                  </Button>
                ) : null}
                {c.estado !== "descartada" && c.negocio_id == null ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={ocupada === String(c.id)}
                    onClick={() => estado(c, "descartada")}
                  >
                    {t("descartar")}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </PaginaAdmin>
  );
};

ColaboracionesPage.path = "/colaboraciones";
